import {getToken} from '../utils/auth';
import {API_BASE} from 'src/api/endpoints';

/**
 * Витринные фото склада — те же обычные HTTP-эндпоинты, что и документы компании
 * (см. api/documents.ts): multipart через Apollo Server 5 не проходит, а метаданные
 * фото приезжают в карточке склада обычным GraphQL-запросом.
 */

/** Бэкенд отдаёт относительный адрес — он не знает домена, за ним прокси (см. photo-url.ts). */
export function photoUrl(url: string): string {
	return `${API_BASE}${url}`;
}

/**
 * Отправка файла с прогрессом. XMLHttpRequest, а не fetch: fetch не сообщает прогресс
 * отправки, а фото до 15 МБ с телефона по мобильной сети идёт заметное время.
 */
export function uploadPhoto(params: {
	warehouseId: string;
	file: File;
	caption?: string;
	onProgress?: (percent: number) => void;
}): Promise<void> {
	const {warehouseId, file, caption, onProgress} = params;
	const query = caption ? `?caption=${encodeURIComponent(caption)}` : '';

	return uploadWithProgress(`/api/warehouses/${warehouseId}/photos${query}`, file, onProgress);
}

/**
 * Файл на эндпоинт загрузки с прогрессом отправки. Общая для фото и видео склада
 * (SKS-735): отказ сервера приходит одним видом — Nest-ответом с `message`.
 */
export function uploadWithProgress(
	path: string,
	file: File,
	onProgress?: (percent: number) => void,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();
		const body = new FormData();

		body.append('file', file);

		request.open('POST', `${API_BASE}${path}`);

		const token = getToken();

		if (token) {
			request.setRequestHeader('Authorization', token);
		}

		request.upload.addEventListener('progress', (event) => {
			if (event.lengthComputable) {
				onProgress?.(Math.round((event.loaded / event.total) * 100));
			}
		});

		request.addEventListener('load', () => {
			if (request.status >= 200 && request.status < 300) {
				resolve();
			} else {
				reject(new Error(errorMessage(request.responseText, request.status)));
			}
		});

		request.addEventListener('error', () => reject(new Error('Не удалось отправить файл')));

		request.send(body);
	});
}

export async function deletePhoto(photoId: string): Promise<void> {
	await send(`${API_BASE}/api/warehouses/photos/${photoId}`, 'DELETE');
}

export async function makePhotoCover(photoId: string): Promise<void> {
	await send(`${API_BASE}/api/warehouses/photos/${photoId}/cover`, 'POST');
}

/**
 * Загрузка тела фото.
 *
 * Отдельным запросом, а не через src у <img>: эндпоинт закрыт AuthGuard'ом, а тег
 * img не умеет отправлять заголовок Authorization. Токен в адресе решил бы это ценой
 * его утечки в историю браузера, логи прокси и Referer, поэтому нет.
 *
 * Повторная загрузка того же фото стоит дёшево: ответ помечен
 * `Cache-Control: private, max-age=86400, immutable`, и fetch берёт его из кэша.
 */
export async function fetchPhotoBlob(url: string, options?: {reload?: boolean}): Promise<Blob> {
	const response = await send(photoUrl(url), 'GET', options);

	return response.blob();
}

export async function send(
	url: string,
	method: string,
	options?: {reload?: boolean},
): Promise<Response> {
	const token = getToken();
	const response = await fetch(url, {
		method,
		headers: token ? {Authorization: token} : {},
		/*
		 * `reload` — когда тело по адресу заменили (см. invalidatePhoto). Обычный запрос
		 * взял бы прежний файл из кэша браузера: у фото сотрудника адрес не меняется,
		 * а ответ помечен `max-age`, и в течение этих минут мы бы показывали старый кадр.
		 */
		cache: options?.reload ? 'reload' : 'default',
	});

	if (!response.ok) {
		throw new Error(errorMessage(await response.text(), response.status));
	}

	return response;
}

/** Бэкенд отвечает обычным Nest-ответом об ошибке — достаём из него текст для пользователя. */
function errorMessage(body: string, status: number): string {
	try {
		const parsed = JSON.parse(body) as {message?: string};

		if (parsed.message) {
			return parsed.message;
		}
	} catch {
		// Не JSON — покажем общий текст ниже
	}

	return status === 413 ? 'Файл слишком большой' : 'Не удалось выполнить действие';
}
